import moment from "moment";
import { memo } from "react";
import { Grey8 } from "styles/antTheme/Colors";

import { BTStatusBadge } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { cn } from "@btnet/utilities-core";

import { isInPortal } from "utilities/portal/portal";

import {
    BTIconInfoCircleErrorOutlined,
    BTIconSyncOutlined,
} from "commonComponents/btWrappers/BTIcon";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { StatusDisplay, type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

export enum InvoiceStatus {
    Unreleased = 1,
    PendingReleased = 2,
    PartiallyPaid = 3,
    Paid = 4,
    Void = 5,
    PastDue = 6,
    Scheduled = 7,
}

export const InvoiceStatusDescription = new Map<InvoiceStatus, string>([
    [InvoiceStatus.Unreleased, "Draft"],
    [InvoiceStatus.PendingReleased, isInPortal({ builder: true }) ? "Pending/Sent" : "Pending"],
    [InvoiceStatus.PartiallyPaid, "Partially paid"],
    [InvoiceStatus.Paid, "Paid"],
    [InvoiceStatus.Void, "Void"],
    [InvoiceStatus.PastDue, "Past due"],
    [InvoiceStatus.Scheduled, "Scheduled"],
]);

export const InvoiceStatusType = (status: InvoiceStatus): StatusTypes => {
    switch (status) {
        case InvoiceStatus.PendingReleased:
            return "pending";
        case InvoiceStatus.PartiallyPaid:
        case InvoiceStatus.Paid:
            return "success";
        case InvoiceStatus.Void:
        case InvoiceStatus.PastDue:
            return "danger";
        case InvoiceStatus.Scheduled:
            return "info";
        default:
            return "";
    }
};

export const IN_APPROVAL_STATUS_TEXT = "In approval";
export const REJECTED_STATUS_TEXT = "Rejected";

const getStatusDetails = (
    status: InvoiceStatus,
    isInApproval: boolean,
    isRejected: boolean,
): { finalStatusType: StatusTypes; finalStatusDesc: string } => {
    // A rejected AIA workflow sends the invoice back to the builder to recall/edit/resend, so the
    // stored status (still Pending/Sent) shouldn't be shown until that resend restarts the workflow.
    if (isRejected) {
        return { finalStatusType: "danger", finalStatusDesc: REJECTED_STATUS_TEXT };
    }
    if (isInApproval) {
        return { finalStatusType: "info", finalStatusDesc: IN_APPROVAL_STATUS_TEXT };
    }
    const finalStatusType = InvoiceStatusType(status);
    const finalStatusDesc = InvoiceStatusDescription.get(status)! ?? "Draft";
    return {
        finalStatusType,
        finalStatusDesc,
    };
};

const renderDetails = (
    status: InvoiceStatus,
    releasedByDate: moment.Moment | undefined,
    releasedByName: string | undefined,
    scheduledReleaseDate: moment.Moment | undefined,
) => {
    if (releasedByDate) {
        return (
            <>
                Sent {releasedByName ? ` by ${releasedByName} ` : ""}
                on <DateDisplay value={releasedByDate} />
            </>
        );
    }

    if (status === InvoiceStatus.Scheduled && scheduledReleaseDate) {
        return (
            <>
                {`Scheduled to be sent to client on ${scheduledReleaseDate.format(
                    "MMMM D, YYYY, h:mm A z",
                )}`}
            </>
        );
    }

    return undefined;
};

const getBdsDisplayType = (oldStatusType: StatusTypes) => {
    if (!oldStatusType) {
        return "default";
    } else if (oldStatusType === "pending") {
        return "warning";
    } else if (oldStatusType === "emergency") {
        return "danger";
    }
    return oldStatusType;
};

interface IOwnerInvoiceStatusProps {
    status: InvoiceStatus;
    releasedByDate: moment.Moment | undefined;
    releasedByName: string | undefined;
    scheduledReleaseDate: moment.Moment | undefined;
    isInApproval?: boolean;
    isRejected?: boolean;
    useBdsBadge?: boolean;
    useSentenceCase?: boolean;
}

const toSentenceCase = (inputString: string) => {
    if (!inputString) {
        return inputString;
    }
    return inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase();
};

export const OwnerInvoiceStatus = memo(function OwnerInvoiceStatusMemo(
    props: IOwnerInvoiceStatusProps,
) {
    const { status, releasedByDate, releasedByName, scheduledReleaseDate, useSentenceCase } = props;
    const isInApproval = props.isInApproval ?? false;
    const isRejected = props.isRejected ?? false;
    const details =
        isInApproval || isRejected
            ? undefined
            : renderDetails(status, releasedByDate, releasedByName, scheduledReleaseDate);
    const statusDetails = getStatusDetails(status, isInApproval, isRejected);

    const description = useSentenceCase
        ? toSentenceCase(statusDetails.finalStatusDesc)
        : statusDetails.finalStatusDesc;
    if (props.useBdsBadge) {
        return (
            <BTStatusBadge
                displayType={getBdsDisplayType(statusDetails.finalStatusType)}
                testid="statusBadge"
                text={description}
                textOnly
            />
        );
    }
    return (
        <StatusDisplay
            statusType={statusDetails.finalStatusType}
            details={details}
            displayMode="relative"
            data-testid="invoiceStatusDisplay"
        >
            {description}
        </StatusDisplay>
    );
});

interface IOwnerInvoiceStatusTagProps {
    status: InvoiceStatus;
    isInApproval?: boolean;
    isRejected?: boolean;
    popoverContent?: React.ReactNode;
}

export const OwnerInvoiceStatusTag = memo(function OwnerInvoiceStatusTagMemo({
    status,
    isInApproval = false,
    isRejected = false,
    popoverContent,
}: IOwnerInvoiceStatusTagProps) {
    const statusDetails = getStatusDetails(status, isInApproval, isRejected);
    return (
        <StatusTagDisplay
            statusType={statusDetails.finalStatusType}
            statusText={statusDetails.finalStatusDesc}
            popoverContent={popoverContent}
        />
    );
});

export interface IOwnerInvoicePaymentStatusProps {
    paymentStatusType: InvoiceStatus;
    isInApproval: boolean;
    isRejected: boolean;
    hasPaymentProcessing: boolean;
    hasIssueWithPayment: boolean;
    "data-testid": string;
    scheduledReleaseDate: string | null;
}

export const OwnerInvoicePaymentStatus = memo(function OwnerInvoicePaymentStatusMemo({
    paymentStatusType,
    isInApproval,
    isRejected,
    hasIssueWithPayment,
    hasPaymentProcessing,
    "data-testid": testId,
    scheduledReleaseDate,
}: IOwnerInvoicePaymentStatusProps) {
    let hoverText: string | null = null;
    const isScheduled = paymentStatusType === InvoiceStatus.Scheduled && scheduledReleaseDate;

    if (hasIssueWithPayment) {
        hoverText = "Issue with payment";
    } else if (hasPaymentProcessing) {
        hoverText = "Processing";
    } else if (isScheduled) {
        const formattedDate = moment(scheduledReleaseDate).format("MMMM D, YYYY, h:mm A z");
        hoverText = `Scheduled to be sent on ${formattedDate}`;
    }

    let iconDisplay: JSX.Element | null = null;
    if (hasIssueWithPayment) {
        iconDisplay = <BTIconInfoCircleErrorOutlined className="padding-left-xs" />;
    } else if (hasPaymentProcessing) {
        iconDisplay = <BTIconSyncOutlined className="padding-left-xs" style={{ color: Grey8 }} />;
    }

    return (
        <div className={cn("flex", "align-items-center")} data-testid={testId}>
            {isScheduled ? (
                <OwnerInvoiceStatusTag
                    status={paymentStatusType}
                    isInApproval={isInApproval}
                    isRejected={isRejected}
                    popoverContent={hoverText}
                />
            ) : (
                <BTPopover content={hoverText} disabled={!hoverText}>
                    <OwnerInvoiceStatusTag
                        status={paymentStatusType}
                        isInApproval={isInApproval}
                        isRejected={isRejected}
                    />
                    {iconDisplay}
                </BTPopover>
            )}
        </div>
    );
});
